import { streetsMateriallyDiffer } from './addressValidation';
import { US_STATE_CODES } from './markets';

// Beacon writes the same key from js/packages/beacon/lib/manual-address-draft.ts.
// The two packages cannot import each other, so the JSON shape is duplicated.
export const MANUAL_ADDRESS_DRAFT_KEY = 'od-manual-address-draft';
export const MANUAL_ADDRESS_DRAFT_TTL_MS = 30 * 60 * 1000;

export type ManualAddressFields = {
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
};

export const MANUAL_ADDRESS_FIELD_KEYS = [
  'street',
  'unit',
  'city',
  'state',
  'postalCode',
] as const satisfies readonly (keyof ManualAddressFields)[];

export type ManualAddressDraft = {
  v: 1;
  searchSessionId?: string;
  query?: string;
  /** Suggestion text. Parsed only to fill fields the typed query left blank. */
  prefillQuery?: string;
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
  edited: boolean;
  expiresAt: number;
  id?: string;
  result?: string[];
};

export type ManualAddressPrefill = ManualAddressFields & {
  id?: string;
  result?: string[];
};

export type ManualAddressHandoff = {
  now: number;
  searchSessionId?: string;
  query?: string;
  /** Suggestion text used as a prefill source. `query` stays the typed input. */
  prefillQuery?: string;
  fields?: Partial<ManualAddressFields>;
  id?: string;
  result?: string[];
  /** A second "Enter address manually" click must not parse over seller edits. */
  keepEdits?: boolean;
};

const EMPTY_FIELDS: ManualAddressFields = {
  street: '',
  unit: '',
  city: '',
  state: '',
  postalCode: '',
};

const US_STATES = new Set<string>(US_STATE_CODES);

// Same labels Beacon uses when it formats a unit onto a confirmation line.
const UNIT_LABEL =
  /^(?:#|apt\.?|apartment|suite|ste\.?|unit|fl\.?|floor|bldg\.?|building|rm\.?|room|dept\.?|lot|slip|trlr|trailer|spc\.?|space)\b/i;

const PO_BOX = /^(?:p\.?\s*o\.?\s*box|post\s+office\s+box)\b/i;
const TRAILING_STATE = /,\s*([A-Za-z]{2})(?:\s+(\d{5})(?:-\d{4})?)?\s*$/;
const TRAILING_CITY_STATE = /,\s*([^,]*?\S)\s+([A-Za-z]{2})(?:\s+(\d{5})(?:-\d{4})?)?\s*$/;
const TRAILING_COUNTRY = /,\s*(?:usa|us|united states(?: of america)?)\.?\s*$/i;
const INLINE_UNIT =
  /^(.*\d.*?)(?:\s+)((?:#|apt\.?|apartment|suite|ste\.?|unit|fl\.?|floor|bldg\.?|building|rm\.?|room)\s+\S.*)$/i;

const blankFields = (): ManualAddressFields => ({ ...EMPTY_FIELDS });

// A comma without a parsed state is a city only when one non-unit segment
// follows the street and that segment is not a ZIP. "Seattle, Washington" and
// "Seattle, 98101" stay in the street; "Kirkland" becomes the city.
const singleTrailingCity = (rest: string, poBox: boolean): boolean => {
  const trailing = rest
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(1)
    .filter((part) => poBox || !UNIT_LABEL.test(part));
  return trailing.length === 1 && !/\d/.test(trailing[0] ?? '');
};

const sameText = (left: string, right: string): boolean =>
  left.trim().toLowerCase() === right.trim().toLowerCase();

const zip5 = (value: string): string => value.replace(/\D/g, '').slice(0, 5);

const unitKey = (value: string): string =>
  value.trim().replace(UNIT_LABEL, '').replace(/^#/, '').trim().toLowerCase();

const trimmedFields = (fields: Partial<ManualAddressFields> | undefined): ManualAddressFields => ({
  street: fields?.street?.trim() ?? '',
  unit: fields?.unit?.trim() ?? '',
  city: fields?.city?.trim() ?? '',
  state: fields?.state?.trim() ?? '',
  postalCode: fields?.postalCode?.trim() ?? '',
});

const hasAnyField = (fields: ManualAddressFields): boolean =>
  Object.values(fields).some((value) => value.trim() !== '');

export function parseManualAddressQuery(query: string): ManualAddressFields {
  const text = query.trim().replace(TRAILING_COUNTRY, '').trim();
  if (!text) return blankFields();

  const poBox = PO_BOX.test(text);
  let state = '';
  let postalCode = '';
  let rest = text;
  const stateMatch = TRAILING_STATE.exec(text);
  const stateCode = stateMatch?.[1]?.toUpperCase();
  const cityStateMatch = TRAILING_CITY_STATE.exec(text);
  const cityStateCode = cityStateMatch?.[2]?.toUpperCase();
  if (stateMatch && stateCode && US_STATES.has(stateCode)) {
    state = stateCode;
    postalCode = stateMatch[2] ?? '';
    rest = text.slice(0, stateMatch.index).trim();
  } else if (cityStateMatch && cityStateCode && US_STATES.has(cityStateCode)) {
    state = cityStateCode;
    postalCode = cityStateMatch[3] ?? '';
    rest = `${text.slice(0, cityStateMatch.index).trim()}, ${cityStateMatch[1] ?? ''}`;
  }

  if (!state) {
    const parsed = splitRest(rest, poBox, singleTrailingCity(rest, poBox));
    return {
      ...blankFields(),
      street: parsed.street || text,
      unit: parsed.unit,
      city: parsed.city,
    };
  }

  if (!rest) return { ...blankFields(), street: text, state, postalCode };
  const parsed = splitRest(rest, poBox, true);
  if (!parsed.street.trim()) return { ...blankFields(), street: text };
  return { street: parsed.street, unit: parsed.unit, city: parsed.city, state, postalCode };
}

function splitRest(
  rest: string,
  poBox: boolean,
  hasState: boolean,
): Pick<ManualAddressFields, 'street' | 'unit' | 'city'> {
  const parts = rest
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const streetPart = parts[0] ?? '';
  if (!streetPart) return { street: '', unit: '', city: '' };

  const unitParts: string[] = [];
  const other: string[] = [];
  for (const part of parts.slice(1)) {
    if (!poBox && UNIT_LABEL.test(part)) unitParts.push(part);
    else other.push(part);
  }

  let street = streetPart;
  let unit = unitParts.join(' ');
  let city = '';
  if (hasState && other.length > 0) {
    city = other[other.length - 1] ?? '';
    const extra = other.slice(0, -1);
    if (extra.length > 0) street = [street, ...extra].join(', ');
  } else if (other.length > 0) {
    street = [street, ...other].join(', ');
  }

  if (!poBox && !unit) {
    const inline = INLINE_UNIT.exec(street);
    const streetBeforeUnit = inline?.[1]?.trim();
    const inlineUnit = inline?.[2]?.trim();
    if (streetBeforeUnit && inlineUnit) {
      street = streetBeforeUnit;
      unit = inlineUnit;
    }
  }

  return { street, unit, city };
}

export function reviewSellerAddress(
  seller: ManualAddressFields,
  casa: ManualAddressFields & { id?: string },
): {
  merged: ManualAddressFields;
  conflicts: Partial<ManualAddressFields>;
  unitDropped: boolean;
} {
  const unitDropped = Boolean(seller.unit.trim() && casa.id?.trim() && !casa.unit.trim());
  const merged: ManualAddressFields = { ...seller };
  const conflicts: Partial<ManualAddressFields> = {};
  const same: Record<keyof ManualAddressFields, (left: string, right: string) => boolean> = {
    street: (left, right) => !streetsMateriallyDiffer(left, right),
    unit: (left, right) => unitKey(left) === unitKey(right),
    city: sameText,
    state: sameText,
    postalCode: (left, right) => zip5(left) === zip5(right),
  };

  MANUAL_ADDRESS_FIELD_KEYS.forEach((key) => {
    const sellerValue = seller[key].trim();
    const casaValue = casa[key].trim();
    if (!sellerValue && casaValue) {
      merged[key] = key === 'postalCode' ? zip5(casaValue) : casaValue;
      return;
    }
    if (key === 'unit' && unitDropped) return;
    if (sellerValue && casaValue && !same[key](seller[key], casa[key])) {
      conflicts[key] = key === 'postalCode' ? zip5(casaValue) : casaValue;
    }
  });

  return { merged, conflicts, unitDropped };
}

export function planManualAddressDraft(
  existing: ManualAddressDraft | null,
  input: ManualAddressHandoff,
): ManualAddressDraft | null {
  const fresh = existing !== null && existing.v === 1 && existing.expiresAt > input.now;
  const sameSession = fresh && (existing?.searchSessionId ?? '') === (input.searchSessionId ?? '');
  const incomingQuery = input.query?.trim() ?? '';
  const storedQuery = existing?.query?.trim() ?? '';
  // An edited draft survives Back only for the address the seller was editing.
  const sameQuery = incomingQuery === '' || incomingQuery === storedQuery;
  if (input.keepEdits && sameSession && existing?.edited && sameQuery) return null;

  const fields = trimmedFields(input.fields);
  const query = input.query?.trim();
  const prefillQuery = input.prefillQuery?.trim();
  const result = input.result?.filter((code) => code.trim() !== '');
  return {
    v: 1,
    ...(input.searchSessionId ? { searchSessionId: input.searchSessionId } : {}),
    ...(query ? { query } : {}),
    ...(prefillQuery && prefillQuery !== query ? { prefillQuery } : {}),
    ...fields,
    ...(input.id ? { id: input.id } : {}),
    ...(result && result.length > 0 ? { result } : {}),
    edited: false,
    expiresAt: input.now + MANUAL_ADDRESS_DRAFT_TTL_MS,
  };
}

export function prefillFields(
  draft: ManualAddressDraft,
  searchSessionId: string | undefined,
  now: number,
): ManualAddressPrefill | null {
  if (draft.v !== 1 || draft.expiresAt <= now) return null;
  if ((draft.searchSessionId ?? '') !== (searchSessionId ?? '')) return null;
  const fields = trimmedFields(draft);
  // A resolved building or suggestion can omit the unit the seller typed.
  // Fill only blank fields, and only before the seller edits. Suggestion text
  // comes first so a short typed query does not replace a parsed city.
  const fromSuggestion =
    !draft.edited && draft.prefillQuery ? parseManualAddressQuery(draft.prefillQuery) : null;
  const fromTyped = !draft.edited && draft.query ? parseManualAddressQuery(draft.query) : null;
  if (hasAnyField(fields)) {
    applyBlankFields(fields, fromSuggestion);
    applyBlankFields(fields, fromTyped);
    return fields;
  }
  if (fromSuggestion) {
    const next = { ...fromSuggestion };
    applyBlankFields(next, fromTyped);
    return next;
  }
  if (draft.query) return parseManualAddressQuery(draft.query);
  return null;
}

const applyBlankFields = (
  fields: ManualAddressFields,
  source: ManualAddressFields | null,
): void => {
  if (!source) return;
  for (const key of MANUAL_ADDRESS_FIELD_KEYS) {
    if (!fields[key] && source[key]) fields[key] = source[key];
  }
};

const parseDraft = (raw: string | null): ManualAddressDraft | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const draft = parsed as Partial<ManualAddressDraft>;
    if (draft.v !== 1) return null;
    return {
      v: 1,
      ...(typeof draft.searchSessionId === 'string'
        ? { searchSessionId: draft.searchSessionId }
        : {}),
      ...(typeof draft.query === 'string' ? { query: draft.query } : {}),
      ...(typeof draft.prefillQuery === 'string' ? { prefillQuery: draft.prefillQuery } : {}),
      street: typeof draft.street === 'string' ? draft.street : '',
      unit: typeof draft.unit === 'string' ? draft.unit : '',
      city: typeof draft.city === 'string' ? draft.city : '',
      state: typeof draft.state === 'string' ? draft.state : '',
      postalCode: typeof draft.postalCode === 'string' ? draft.postalCode : '',
      edited: draft.edited === true,
      expiresAt: typeof draft.expiresAt === 'number' ? draft.expiresAt : 0,
      ...(typeof draft.id === 'string' && draft.id ? { id: draft.id } : {}),
      ...(Array.isArray(draft.result)
        ? {
            result: draft.result.filter(
              (code): code is string => typeof code === 'string' && code !== '',
            ),
          }
        : {}),
    };
  } catch {
    return null;
  }
};

const draftStorage = (): Storage | null => {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
};

// Beacon on localhost:3030 and this page on localhost:3011 do not share
// sessionStorage. Beacon copies the draft into this cookie, which the browser
// sends to every localhost port.
const readLocalhostDraftCookie = (): string | null => {
  if (typeof document === 'undefined' || globalThis.location?.hostname !== 'localhost') return null;
  const prefix = `${MANUAL_ADDRESS_DRAFT_KEY}=`;
  const part = document.cookie.split('; ').find((item) => item.startsWith(prefix));
  if (!part) return null;
  try {
    return decodeURIComponent(part.slice(prefix.length));
  } catch {
    return null;
  }
};

const readStoredDraft = (): string | null => {
  const storage = draftStorage();
  if (!storage) return null;
  try {
    return storage.getItem(MANUAL_ADDRESS_DRAFT_KEY);
  } catch {
    return null;
  }
};

const draftMatchesSession = (raw: string | null, searchSessionId: string): boolean => {
  if (!raw) return false;
  return (parseDraft(raw)?.searchSessionId ?? '') === searchSessionId;
};

const freshSessionMatch = (raw: string | null, searchSessionId: string, now: number): boolean => {
  const draft = parseDraft(raw);
  return draft !== null && draftMatchesSession(raw, searchSessionId) && draft.expiresAt > now;
};

// Callers that update an in-progress form keep the stored draft. Prefill asks
// for a session: a fresh stored draft wins, then a fresh cookie. An earlier
// session in storage must not hide the cookie Beacon just wrote.
const readDraftRaw = (searchSessionId?: string, now = Date.now()): string | null => {
  const stored = readStoredDraft();
  const cookie = readLocalhostDraftCookie();
  if (searchSessionId === undefined) return stored ?? cookie;
  if (freshSessionMatch(stored, searchSessionId, now)) return stored;
  if (freshSessionMatch(cookie, searchSessionId, now)) return cookie;
  if (draftMatchesSession(stored, searchSessionId)) return stored;
  if (draftMatchesSession(cookie, searchSessionId)) return cookie;
  return null;
};

const writeDraftRaw = (value: string): void => {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.setItem(MANUAL_ADDRESS_DRAFT_KEY, value);
  } catch {
    // A blocked or full sessionStorage must not stop navigation or submit.
  }
};

const clearExpiredDraft = (raw: string | null): void => {
  const stored = readStoredDraft();
  if (raw && raw === stored) {
    const storage = draftStorage();
    if (storage) {
      try {
        storage.removeItem(MANUAL_ADDRESS_DRAFT_KEY);
      } catch {
        // Leaving an expired draft is better than failing the page.
      }
    }
  }
  if (raw && raw === readLocalhostDraftCookie()) {
    clearLocalhostDraftCookie();
  }
};

export function readManualAddressPrefill(
  searchSessionId?: string,
  now = Date.now(),
): ManualAddressPrefill | null {
  const raw = readDraftRaw(searchSessionId, now);
  const draft = parseDraft(raw);
  if (!draft) return null;
  if (draft.expiresAt <= now) {
    clearExpiredDraft(raw);
    return null;
  }
  const fields = prefillFields(draft, searchSessionId, now);
  if (!fields) return null;
  return {
    ...fields,
    ...(draft.id ? { id: draft.id } : {}),
    ...(draft.result && draft.result.length > 0 ? { result: draft.result } : {}),
  };
}

export function rememberManualAddressHandoff(
  input: Omit<ManualAddressHandoff, 'now'> & { now?: number },
): void {
  const existing = parseDraft(readDraftRaw());
  const next = planManualAddressDraft(existing, { ...input, now: input.now ?? Date.now() });
  if (!next) return;
  writeDraftRaw(JSON.stringify(next));
}

export function markManualAddressDraftEdited(
  fields: ManualAddressFields,
  searchSessionId?: string,
  now = Date.now(),
  extras?: { id?: string; result?: string[] },
): void {
  const existing = parseDraft(readDraftRaw());
  const searchSession = searchSessionId ?? existing?.searchSessionId;
  const id = extras ? extras.id : existing?.id;
  const result = extras ? extras.result : existing?.result;
  const next: ManualAddressDraft = {
    v: 1,
    ...(searchSession ? { searchSessionId: searchSession } : {}),
    ...(existing?.query ? { query: existing.query } : {}),
    ...trimmedFields(fields),
    ...(id ? { id } : {}),
    ...(result && result.length > 0 ? { result } : {}),
    edited: true,
    expiresAt: now + MANUAL_ADDRESS_DRAFT_TTL_MS,
  };
  writeDraftRaw(JSON.stringify(next));
}

let cachedDraftRaw: string | null | undefined;
let cachedDraftSession: string | undefined;
let cachedDraft: ManualAddressPrefill | null = null;

export function subscribeManualAddressDraft(): () => void {
  return () => {};
}

/** Cached so useSyncExternalStore does not loop. The server snapshot stays empty. */
export function readManualAddressDraftSnapshot(
  searchSessionId?: string,
): ManualAddressPrefill | null {
  const raw = readDraftRaw(searchSessionId);
  if (raw === cachedDraftRaw && searchSessionId === cachedDraftSession) return cachedDraft;
  cachedDraftRaw = raw;
  cachedDraftSession = searchSessionId;
  cachedDraft = readManualAddressPrefill(searchSessionId);
  return cachedDraft;
}

const clearLocalhostDraftCookie = (): void => {
  if (typeof document === 'undefined' || globalThis.location?.hostname !== 'localhost') return;
  document.cookie = `${MANUAL_ADDRESS_DRAFT_KEY}=; Path=/; Max-Age=0; SameSite=Lax`;
};

export function clearManualAddressDraft(): void {
  const storage = draftStorage();
  if (storage) {
    try {
      storage.removeItem(MANUAL_ADDRESS_DRAFT_KEY);
    } catch {
      // A failed clear must not turn a successful handoff into an error.
    }
  }
  clearLocalhostDraftCookie();
}
